import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { MapPin, Car, Calendar, AlertTriangle, Clock } from "lucide-react";

const STATUS_COLORS = {
  verified: "bg-emerald-500",
  corroborated: "bg-[#F59E0B]",
  reported: "bg-yellow-400",
  expired: "bg-slate-300",
  no_longer_here: "bg-red-400",
};

const STATUS_LABELS = {
  verified: "Verified",
  corroborated: "Corroborated",
  reported: "Reported",
  expired: "Expired",
  no_longer_here: "Moved",
};

function relativeTime(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function AccountabilityLog({ businessId, scheduleConflict }) {
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/trucks/spot-history/${businessId}`);
        setSpots(res.data?.spots || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [businessId]);

  if (loading) {
    return <div className="py-4 text-center text-xs text-slate-300">Loading spot history...</div>;
  }

  if (spots.length === 0) {
    return (
      <div className="py-4 text-center" data-testid="accountability-empty">
        <p className="text-xs text-slate-400">No spots yet today</p>
        <p className="text-[10px] text-slate-300 mt-1">Be the first to report this truck</p>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="accountability-log">
      {/* Schedule conflict notice */}
      {scheduleConflict && (
        <div className="flex items-start gap-2 mb-3 p-2 bg-amber-50 rounded-lg" data-testid="schedule-conflict-notice">
          <AlertTriangle className="w-4 h-4 text-[#F59E0B] mt-0.5 shrink-0" />
          <p className="text-[10px] text-amber-700">Spotted at a different location than scheduled</p>
        </div>
      )}

      {/* Timeline */}
      {spots.map((spot, i) => (
        <div key={spot.spot_id} className="flex gap-3" data-testid={`spot-entry-${spot.spot_id}`}>
          {/* Timeline line + dot */}
          <div className="flex flex-col items-center">
            <div className={`w-2.5 h-2.5 rounded-full ${STATUS_COLORS[spot.verification_status] || "bg-slate-300"} shrink-0 mt-1`} />
            {i < spots.length - 1 && <div className="w-px flex-1 bg-slate-100 mt-1" />}
          </div>

          {/* Content */}
          <div className="flex-1 pb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-700">{spot.display_name}</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400 font-medium">
                {STATUS_LABELS[spot.verification_status] || spot.verification_status}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {spot.spot_type === "on_foot" ? (
                <MapPin className="w-3 h-3 text-slate-300" />
              ) : (
                <Car className="w-3 h-3 text-slate-300" />
              )}
              <span className="text-[10px] text-slate-400">{relativeTime(spot.spotted_at)}</span>
              {spot.estimated_still_here && spot.estimated_still_here !== "unknown" && (
                <span className="text-[10px] text-slate-300 flex items-center gap-0.5">
                  <Clock className="w-2.5 h-2.5" /> {spot.estimated_still_here}
                </span>
              )}
              {spot.confirmed_by_count > 0 && (
                <span className="text-[10px] text-emerald-500 font-medium">+{spot.confirmed_by_count} confirmed</span>
              )}
            </div>
            {spot.address && <p className="text-[10px] text-slate-300 mt-0.5">{spot.address}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}
